import React, { useState } from 'react';
import {
    Typography,
    Box
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../store/store';
import { useCreatePhysicalCardMutation, useFetchDataQuery } from '../../../services/services';
import CreatePhysicalCardForm, { PhysicalCardFormData, PhysicalCardRequestData } from './CreatePhysicalCardForm';

// Card service interfaces
interface CreatePhysicalCardResponse {
    success: boolean;
    message: string;
}

interface CreatePhysicalCardActionProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}

const CreatePhysicalCardAction: React.FC<CreatePhysicalCardActionProps> = ({ isOpen, onClose, onSuccess }) => {
    const { currentUserState } = useSelector((state: RootState) => state.currentUserState);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState<PhysicalCardFormData>({
        userName: '',
        userSurName: '',
        email: '',
        cityName: '',
        countyName: '',
        neighborhood: '',
        street: '',
        phoneNumber: '',
        cardType: '',
        buildingNo: '',
        cityId: 0,
        countyId: 0,
        neighborhoodId: 0,
        companyId: currentUserState?.companyId || 0 // Use current user's company ID or default to 3284
    });
    const [requestData, setRequestData] = useState<PhysicalCardRequestData | null>(null);
    const [isFormValid, setIsFormValid] = useState(false);

    const [createPhysicalCard] = useCreatePhysicalCardMutation();

    // Refetch the card list after successful creation
    const listUrl = `/Card/CustomerCardsV2?customerId=${currentUserState?.companyId}`;
    const [requestBody] = useState({
        filters: [],
        pageSize: 25,
        page: 1,
        orderBy: "",
        orderDesc: false,
        customerId: currentUserState?.companyId || undefined,
    });
    const { refetch } = useFetchDataQuery({ url: listUrl, filter: requestBody, method: "POST" }, { skip: !requestBody?.customerId });

    const handleFormChange = (newFormData: PhysicalCardFormData) => {
        setFormData(newFormData);
    };

    const handleValidationChange = (isValid: boolean) => {
        setIsFormValid(isValid);
    };

    const handleRequestDataChange = (newRequestData: PhysicalCardRequestData | null) => {
        setRequestData(newRequestData);
    };

    const handleConfirm = async () => {
        if (!isFormValid || !requestData) {
            enqueueSnackbar('Lütfen tüm alanları doldurun.', {
                variant: 'error'
            });
            return false;
        }

        try {
            setLoading(true);
            // Use the transformed request data that matches the desired format
            const result = await createPhysicalCard(requestData);
            const response: CreatePhysicalCardResponse | undefined = (result as any).data;

            if (response?.success) {
                enqueueSnackbar(response.message, {
                    variant: 'success'
                });

                // Refresh the card list
                refetch();
                onSuccess?.();
                handleClose();
                return true;
            } else {
                enqueueSnackbar(response?.message || 'Fiziki kart oluşturulurken bir hata oluştu', {
                    variant: 'error'
                });
                return false;
            }
        } catch (error: any) {
            const errorMessage = error?.error?.message || error?.message || 'Bilinmeyen bir hata oluştu';
            enqueueSnackbar(errorMessage, {
                variant: 'error'
            });
            return false;
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setFormData({
            userName: '',
            userSurName: '',
            email: '',
            cityName: '',
            countyName: '',
            phoneNumber: '',
            cardType: '',
            neighborhood: '',
            street: '',
            buildingNo: '',
            cityId: 0,
            countyId: 0,
            neighborhoodId: 0,
            companyId: 3284
        });
        setRequestData(null);
        setIsFormValid(false);
        onClose();
    };

    return (
        <ConfirmationModal
            isOpen={isOpen}
            onClose={handleClose}
            onConfirm={handleConfirm}
            title="🏪 Fiziki Kart Oluştur"
            isLoading={loading}
            confirmButtonText={loading ? 'Oluşturuluyor...' : 'Oluştur'}
            closeButtonText="Vazgeç"
            optionalWidth="800px"
        >
            <Box sx={{ py: 1 }}>
                <Typography variant="body2" sx={{
                    color: '#64748b',
                    mb: 2,
                    textAlign: 'center'
                }}>
                    Fiziki kartınız için gerekli bilgileri doldurun. Kart belirlediğiniz adrese kargo ile gönderilecektir.
                </Typography>

                <CreatePhysicalCardForm
                    onFormChange={handleFormChange}
                    onValidationChange={handleValidationChange}
                    onRequestDataChange={handleRequestDataChange}
                />
            </Box>
        </ConfirmationModal>
    );
};

export default CreatePhysicalCardAction;
